// AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [tokenExpiry, setTokenExpiry] = useState(null);
    const [refreshToken, setRefreshToken] = useState(null);
    const [role, setRole] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        console.log('[AuthContext] Initializing context...');
        setIsLoading(true); // Explicitly set loading true at the start of initialization
        const activeProfileRoleFromEnv = process.env.REACT_APP_USER_ROLE;
        const storedAppProfileRole = localStorage.getItem('appProfileRole');

        console.log(`[AuthContext] Environment profile role (REACT_APP_USER_ROLE): "${activeProfileRoleFromEnv}"`);
        console.log(`[AuthContext] Stored appProfileRole from localStorage: "${storedAppProfileRole}"`);

        if (activeProfileRoleFromEnv && storedAppProfileRole && activeProfileRoleFromEnv !== storedAppProfileRole) {
            console.warn(
                `[AuthContext] Profile role mismatch detected. Env: "${activeProfileRoleFromEnv}", Stored: "${storedAppProfileRole}". NOT clearing localStorage to preserve user authentication.`
            );
            // REMOVED: localStorage.clear() - This was causing authentication data loss for other roles
            // Instead, we'll just update the stored profile role without clearing auth data
        } else if (activeProfileRoleFromEnv && !storedAppProfileRole) {
            console.log(`[AuthContext] No storedAppProfileRole found in localStorage. Current env role: "${activeProfileRoleFromEnv}". Not clearing localStorage due to mismatch (fresh profile session).`);
        } else if (!activeProfileRoleFromEnv) {
            console.warn(`[AuthContext] REACT_APP_USER_ROLE is not set in the environment. Cannot properly manage profile role in localStorage or clear on mismatch.`);
        } else if (activeProfileRoleFromEnv && storedAppProfileRole && activeProfileRoleFromEnv === storedAppProfileRole) {
            console.log(`[AuthContext] Profile roles match. Env: "${activeProfileRoleFromEnv}", Stored: "${storedAppProfileRole}". Not clearing localStorage due to mismatch.`);
        } else {
            console.log(`[AuthContext] No condition met to clear localStorage based on profile role mismatch. Env: "${activeProfileRoleFromEnv}", Stored: "${storedAppProfileRole}".`);
        }

        // Always (re-)store the current environment's profile role.
        // If localStorage was cleared, this sets it for the new profile.
        // If not cleared, it just confirms/updates it.
        if (activeProfileRoleFromEnv) {
            localStorage.setItem('appProfileRole', activeProfileRoleFromEnv);
            console.log(`[AuthContext] Set/updated appProfileRole in localStorage to: "${activeProfileRoleFromEnv}"`);
        } else {
            // If REACT_APP_USER_ROLE is not set, we might want to remove appProfileRole
            // or handle it as an error, as profile-specific logic might break.
            // localStorage.removeItem('appProfileRole');
        }

        const storedToken = localStorage.getItem('authToken');
        const storedTokenExpiry = localStorage.getItem('tokenExpiry');
        const storedRefreshToken = localStorage.getItem('refreshToken');
        const storedRole = localStorage.getItem('userRole');
        const storedUserId = localStorage.getItem('userId');
        const storedUser = localStorage.getItem('authUser');

        console.log(`[AuthContext] Attempting to load token from localStorage. Found: ${!!storedToken}`);

        // Check if token exists and is not expired
        const isTokenValid = storedToken && storedTokenExpiry && new Date(storedTokenExpiry) > new Date();

        if (isTokenValid && storedRole && storedUserId) {
            setToken(storedToken);
            setTokenExpiry(storedTokenExpiry);
            setRefreshToken(storedRefreshToken);
            setRole(storedRole);
            setUserId(storedUserId);
            if (storedUser) {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    setUser(parsedUser);
                } catch (error) {
                    console.error('[AuthContext] Failed to parse stored user data:', error);
                    setUser(null);
                    localStorage.removeItem('authUser');
                }
            }
            console.log('[AuthContext] User authenticated from localStorage data.');
        } else {
            // This block executes if localStorage was cleared or no valid auth data was found.
            // Ensure all auth-related states are reset to their initial unauthenticated values.
            setToken(null);
            setRole(null);
            setUserId(null);
            setUser(null);
            console.log('[AuthContext] No valid authentication data in localStorage. User set to unauthenticated.');
        }
        setIsLoading(false); // Set loading to false after attempting to load from localStorage
        console.log('[AuthContext] Context initialization complete.');
    }, []);

    const login = async (newToken, newRole, newUserId, userData = null, expiresIn = 3600, newRefreshToken = null) => {
        const activeProfileRoleFromEnv = process.env.REACT_APP_USER_ROLE;
        const expiryDate = new Date(Date.now() + expiresIn * 1000).toISOString();

        // First update localStorage
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('tokenExpiry', expiryDate);
        if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
        }
        localStorage.setItem('userRole', newRole);
        localStorage.setItem('userId', newUserId);
        if (userData) {
            localStorage.setItem('authUser', JSON.stringify(userData));
        } else {
            localStorage.removeItem('authUser');
        }
        if (activeProfileRoleFromEnv) {
            localStorage.setItem('appProfileRole', activeProfileRoleFromEnv);
        }

        // Then update state in a single batch
        setToken(newToken);
        setRole(newRole);
        setUserId(newUserId);
        setUser(userData);
        setTokenExpiry(expiryDate);
        if (newRefreshToken) {
            setRefreshToken(newRefreshToken);
        }

        // Force React to process state updates before continuing
        await new Promise(resolve => setTimeout(resolve, 0));
    };

    const logout = () => {
        localStorage.clear();
        setToken(null);
        setTokenExpiry(null);
        setRefreshToken(null);
        setRole(null);
        setUserId(null);
        setUser(null);
        // After logout, if the app reloads/AuthContext re-initializes,
        // the useEffect will run and set the appProfileRole to the current process.env.REACT_APP_USER_ROLE,
        // which is the correct behavior.
    };

    const updateUserInContext = (userDataToStore) => {
        // userDataToStore is the object constructed by the caller (e.g., ProfileUserScreen)
        // that represents the desired state of the user after updates.
        // It should already include the user's 'id' and other essential fields.
        if (!userDataToStore || !userDataToStore.id) {
            console.error("[AuthContext] Cannot update user in context: userDataToStore or its id is missing.");
            return;
        }

        // Create a completely new user object to ensure React detects a state change.
        // Start by taking all properties from the existing user in context.
        // Then, overwrite/add properties from the server response.
        // Finally, derive any UI-specific fields.
        setUser(currentUserState => { // Use functional update for safety, though less critical if caller constructs fully
            let processedUser = {
                ...userDataToStore, // Start with the data provided by the caller, which is now the source of truth
            };

            // Derive 'name' from 'username'
            if (processedUser.username) {
                processedUser.name = processedUser.username;
            }

            // Derive 'image' (full URL) from 'avatar_file_id'
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
            if (processedUser.avatar_file_id) {
                processedUser.image = `${API_BASE_URL}/files/serve/${processedUser.avatar_file_id}`;
            } else {
                processedUser.image = null; // Or a default placeholder
            }

            console.log('[AuthContext] updateUserInContext - FINAL processedUser to be stored:', JSON.parse(JSON.stringify(processedUser)));
            localStorage.setItem('authUser', JSON.stringify(processedUser));
            return processedUser; // Return the new state, ensuring it's a new object reference
        });
    };


    // Function to refresh access token
    const refreshAccessToken = async () => {
        if (!refreshToken) {
            console.error('[AuthContext] No refresh token available');
            logout();
            return null;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken }),
            });

            if (!response.ok) {
                throw new Error('Failed to refresh token');
            }

            const data = await response.json();
            login(data.accessToken, role, userId, user, data.expiresIn, data.refreshToken);
            return data.accessToken;
        } catch (error) {
            console.error('[AuthContext] Token refresh failed:', error);
            logout();
            return null;
        }
    };

    // Function to check token validity before API calls
    const getValidToken = async () => {
        if (token && tokenExpiry && new Date(tokenExpiry) > new Date()) {
            return token;
        }
        return await refreshAccessToken();
    };

    const value = {
        user,
        token,
        role,
        userId,
        isLoading,
        isAuthenticated: !!token && new Date(tokenExpiry) > new Date(),
        login,
        logout,
        updateUserInContext,
        getValidToken,
        refreshAccessToken,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
